/** @jsxImportSource @emotion/react */
import { Button, Checkbox } from '@mui/material';
import { CheckBoxDiv, ControlBtnGroup } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { st } from 'components/inputs/CustomInput';
import { useMechUtTypes } from 'hooks/useMechUtTypes';
import { usePrdnProcOrder } from 'hooks/usePrdnProcOrder';
import { useUtComparisonStore } from '../store/useUtComparisonStore';
import { exportExcel } from '../utils/exportExcel';
import { exportPpt } from '../utils/exportPpt';
import { sortAnalysisRows } from '../utils/sortRows';

/**
 * 표시 옵션(차이만/1라인별) + Excel·PPT 산출.
 *
 * ② UT 항목 선택 영역의 **본문 안**에 놓인다 — 그 영역을 접으면 같이 접혀야 해서다
 * (헤더에 두면 접어도 남는다). 분석 시작/재실행·초기화는 ① UT Matrix 선택 영역에 있다.
 */
const ControlsBar = () => {
  const analysisResult = useUtComparisonStore((s) => s.analysisResult);
  const versionLabels = useUtComparisonStore((s) => s.versionLabels);
  const isPerLineMode = useUtComparisonStore((s) => s.isPerLineMode);
  const diffOnly = useUtComparisonStore((s) => s.diffOnly);
  const perLineChecked = useUtComparisonStore((s) => s.perLineChecked);
  const setDiffOnly = useUtComparisonStore((s) => s.setDiffOnly);
  const setPerLineChecked = useUtComparisonStore((s) => s.setPerLineChecked);
  const analyzing = useUtComparisonStore((s) => s.analyzing);
  // 산출물도 화면과 같은 항목·순서를 쓰도록 공통코드 목록을 넘긴다
  const utTypes = useMechUtTypes();
  const procOrder = usePrdnProcOrder();

  /**
   * 산출물의 행 순서를 ④ 표와 맞춘다.
   *
   * 산출 함수는 스토어의 analysisResult(원본)를 그대로 받으므로, 여기서 정렬하지 않으면
   * 화면과 엑셀/PPT의 행 순서가 달라진다. 표시 옵션(차이만/UT 항목)은 산출물에 적용하지
   * 않는 기존 동작 그대로 두고, 순서만 맞춘다.
   */
  const exportRows = () =>
    analysisResult ? sortAnalysisRows(analysisResult, procOrder, isPerLineMode) : [];

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 10 }}>
      <ControlBtnGroup>
        <Button
          css={IconButton.button}
          className="Exceldown"
          disabled={!analysisResult}
          onClick={() =>
            analysisResult &&
            exportExcel(exportRows(), versionLabels, isPerLineMode, utTypes, procOrder)
          }
          disableRipple
        >
          Excel 산출
        </Button>
        <Button
          css={IconButton.button}
          className="ppt"
          disabled={!analysisResult}
          onClick={() => analysisResult && exportPpt(exportRows(), versionLabels, utTypes)}
          disableRipple
        >
          PPT 산출
        </Button>
        {/* 산출 버튼 묶음과 표시 옵션 묶음을 눈으로 구분되게 띄운다 */}
        <CheckBoxDiv style={{ marginLeft: 20 }}>
          <Checkbox
            id="utDiffOnly"
            css={st.checkbox}
            className="custom"
            checked={diffOnly}
            onChange={(e) => setDiffOnly(e.target.checked)}
            disableRipple
          />
          <label htmlFor="utDiffOnly">차이 있는 항목만 표시</label>
        </CheckBoxDiv>
        {/*
          저장된 리포트 조회 중에도 노출한다 — 켜면 원본을 다시 읽어 라인당 사용량으로 재계산하고,
          끄면 저장 당시 스냅샷으로 되돌린다(스토어 setPerLineChecked). DB에는 쓰지 않는다.
        */}
        <CheckBoxDiv style={{ opacity: analyzing ? 0.5 : 1 }}>
          <Checkbox
            id="utPerLine"
            css={st.checkbox}
            className="custom"
            checked={perLineChecked}
            disabled={analyzing}
            onChange={(e) => setPerLineChecked(e.target.checked)}
            disableRipple
          />
          <label htmlFor="utPerLine">1라인별 사용량 비교{analyzing && ' (재분석 중…)'}</label>
        </CheckBoxDiv>
      </ControlBtnGroup>
    </div>
  );
};

export default ControlsBar;
